// Web-only strings that have no mobile counterpart. Merged over the shared
// mobile locale JSON at init. Keep this list short — prefer reusing mobile
// keys so translations stay in one place.

type WebStrings = Record<string, string>;

export const webStrings: Record<string, WebStrings> = {
  en: {
    WebLoginSubtitle:
      'Sign in to access your voice notes on any device.',
    WebSignOut:
      'Sign out',
    WebUpgradeOnMobile:
      'Owlly Pro is purchased in the Owlly mobile app. Subscribe on your phone, sign in with the same account here, and Pro unlocks on the web too.',
    WebGetTheApp: 'Get the mobile app',
    WebDropFiles: 'Drop an audio file or PDF to create a note',
    WebDownloadAudio: 'Download Audio',
    WebExportPDF: 'Save as PDF',
    WebMicDenied:
      'Microphone access was denied. Allow it in your browser settings and try again.',
    WebRecordingUnsupported:
      'This browser does not support microphone recording.',
    WebKeepTabOpen: 'Keep this tab open until the upload finishes.',
    WebGuestAccount: 'Guest account',
    WebGuestWarning:
      'Your notes are tied to this browser. Sign in to keep them safe and use them on your phone.',
    WebShortcutsHint: 'Shortcuts: N new note · / search',
    WebSameAccountHint:
      'Already subscribed on your phone? Sign in with the same Apple or Google account to unlock Pro here.',
    WebSharedWithOwlly: 'Made with Owlly',
    WebSharedCtaTitle: 'Turn voice notes into summaries, quizzes and flashcards',
    WebSharedCtaSub: 'Get Owlly free on iPhone, Android or the web.',
    WebSharedOpenApp: 'Open Owlly',
    WebShareNotFoundTitle: "This note isn't available",
    WebShareNotFoundBody: 'The link may be broken, or the owner stopped sharing it.',
    WebShareNote: 'Share link',
    WebStopSharing: 'Stop sharing',
    WebStopSharingDone: 'Public link removed',
  },
  de: {
    WebLoginSubtitle:
      'Melde dich an, um auf deine Sprachnotizen auf jedem Gerät zuzugreifen.',
    WebSignOut:
      'Abmelden',
    WebUpgradeOnMobile:
      'Owlly Pro wird in der Owlly-App gekauft. Abonniere auf deinem Handy, melde dich hier mit demselben Konto an, und Pro wird auch im Web freigeschaltet.',
    WebGetTheApp: 'Mobile App holen',
    WebDropFiles: 'Audiodatei oder PDF hier ablegen, um eine Notiz zu erstellen',
    WebDownloadAudio: 'Audio herunterladen',
    WebExportPDF: 'Als PDF speichern',
    WebMicDenied:
      'Mikrofonzugriff wurde verweigert. Erlaube ihn in den Browsereinstellungen und versuche es erneut.',
    WebRecordingUnsupported:
      'Dieser Browser unterstützt keine Mikrofonaufnahmen.',
    WebKeepTabOpen: 'Lass diesen Tab geöffnet, bis der Upload abgeschlossen ist.',
    WebGuestAccount: 'Gastkonto',
    WebGuestWarning:
      'Deine Notizen sind an diesen Browser gebunden. Melde dich an, um sie zu sichern und auf deinem Handy zu nutzen.',
    WebShortcutsHint: 'Kürzel: N neue Notiz · / Suche',
    WebSameAccountHint:
      'Schon auf dem Handy abonniert? Melde dich mit demselben Apple- oder Google-Konto an, um Pro freizuschalten.',
    WebSharedWithOwlly: 'Mit Owlly erstellt',
    WebSharedCtaTitle: 'Verwandle Sprachnotizen in Zusammenfassungen, Quizze und Karteikarten',
    WebSharedCtaSub: 'Hol dir Owlly kostenlos für iPhone, Android oder im Web.',
    WebSharedOpenApp: 'Owlly öffnen',
    WebShareNotFoundTitle: 'Diese Notiz ist nicht verfügbar',
    WebShareNotFoundBody: 'Der Link ist möglicherweise defekt oder der Besitzer hat die Freigabe beendet.',
    WebShareNote: 'Link teilen',
    WebStopSharing: 'Freigabe beenden',
    WebStopSharingDone: 'Öffentlicher Link entfernt',
  },
  es: {
    WebLoginSubtitle:
      'Inicia sesión para acceder a tus notas de voz en cualquier dispositivo.',
    WebSignOut:
      'Cerrar sesión',
    WebUpgradeOnMobile:
      'Owlly Pro se compra en la app móvil de Owlly. Suscríbete en tu teléfono, inicia sesión aquí con la misma cuenta y Pro se desbloqueará también en la web.',
    WebGetTheApp: 'Descargar la app móvil',
    WebDropFiles: 'Suelta un archivo de audio o PDF para crear una nota',
    WebDownloadAudio: 'Descargar audio',
    WebExportPDF: 'Guardar como PDF',
    WebMicDenied:
      'Se denegó el acceso al micrófono. Permítelo en la configuración del navegador e inténtalo de nuevo.',
    WebRecordingUnsupported:
      'Este navegador no admite la grabación con micrófono.',
    WebKeepTabOpen: 'Mantén esta pestaña abierta hasta que termine la subida.',
    WebGuestAccount: 'Cuenta de invitado',
    WebGuestWarning:
      'Tus notas están vinculadas a este navegador. Inicia sesión para protegerlas y usarlas en tu teléfono.',
    WebShortcutsHint: 'Atajos: N nueva nota · / buscar',
    WebSameAccountHint:
      '¿Ya estás suscrito en tu teléfono? Inicia sesión con la misma cuenta de Apple o Google para desbloquear Pro aquí.',
    WebSharedWithOwlly: 'Creado con Owlly',
    WebSharedCtaTitle: 'Convierte notas de voz en resúmenes, cuestionarios y tarjetas',
    WebSharedCtaSub: 'Consigue Owlly gratis en iPhone, Android o la web.',
    WebSharedOpenApp: 'Abrir Owlly',
    WebShareNotFoundTitle: 'Esta nota no está disponible',
    WebShareNotFoundBody: 'El enlace puede estar roto o el propietario dejó de compartirla.',
    WebShareNote: 'Compartir enlace',
    WebStopSharing: 'Dejar de compartir',
    WebStopSharingDone: 'Enlace público eliminado',
  },
  fr: {
    WebLoginSubtitle:
      'Connectez-vous pour accéder à vos notes vocales sur tous vos appareils.',
    WebSignOut:
      'Se déconnecter',
    WebUpgradeOnMobile:
      "Owlly Pro s'achète dans l'app mobile Owlly. Abonnez-vous sur votre téléphone, connectez-vous ici avec le même compte et Pro sera aussi débloqué sur le web.",
    WebGetTheApp: "Obtenir l'app mobile",
    WebDropFiles: 'Déposez un fichier audio ou PDF pour créer une note',
    WebDownloadAudio: "Télécharger l'audio",
    WebExportPDF: 'Enregistrer en PDF',
    WebMicDenied:
      "L'accès au micro a été refusé. Autorisez-le dans les réglages du navigateur puis réessayez.",
    WebRecordingUnsupported:
      "Ce navigateur ne prend pas en charge l'enregistrement au micro.",
    WebKeepTabOpen: "Gardez cet onglet ouvert jusqu'à la fin de l'envoi.",
    WebGuestAccount: 'Compte invité',
    WebGuestWarning:
      'Vos notes sont liées à ce navigateur. Connectez-vous pour les protéger et les retrouver sur votre téléphone.',
    WebShortcutsHint: 'Raccourcis : N nouvelle note · / recherche',
    WebSameAccountHint:
      'Déjà abonné sur votre téléphone ? Connectez-vous avec le même compte Apple ou Google pour débloquer Pro ici.',
    WebSharedWithOwlly: 'Créé avec Owlly',
    WebSharedCtaTitle: 'Transformez vos notes vocales en résumés, quiz et cartes mémo',
    WebSharedCtaSub: 'Obtenez Owlly gratuitement sur iPhone, Android ou le web.',
    WebSharedOpenApp: 'Ouvrir Owlly',
    WebShareNotFoundTitle: "Cette note n'est pas disponible",
    WebShareNotFoundBody: 'Le lien est peut-être rompu, ou le propriétaire a cessé de la partager.',
    WebShareNote: 'Partager le lien',
    WebStopSharing: 'Arrêter le partage',
    WebStopSharingDone: 'Lien public supprimé',
  },
  it: {
    WebLoginSubtitle:
      'Accedi per usare le tue note vocali su qualsiasi dispositivo.',
    WebSignOut:
      'Esci',
    WebUpgradeOnMobile:
      "Owlly Pro si acquista nell'app mobile di Owlly. Abbonati sul telefono, accedi qui con lo stesso account e Pro si sbloccherà anche sul web.",
    WebGetTheApp: "Scarica l'app mobile",
    WebDropFiles: 'Trascina un file audio o PDF per creare una nota',
    WebDownloadAudio: 'Scarica audio',
    WebExportPDF: 'Salva come PDF',
    WebMicDenied:
      "L'accesso al microfono è stato negato. Consentilo nelle impostazioni del browser e riprova.",
    WebRecordingUnsupported:
      'Questo browser non supporta la registrazione dal microfono.',
    WebKeepTabOpen: 'Tieni aperta questa scheda finché il caricamento non termina.',
    WebGuestAccount: 'Account ospite',
    WebGuestWarning:
      'Le tue note sono legate a questo browser. Accedi per proteggerle e usarle sul telefono.',
    WebShortcutsHint: 'Scorciatoie: N nuova nota · / cerca',
    WebSameAccountHint:
      'Già abbonato sul telefono? Accedi con lo stesso account Apple o Google per sbloccare Pro qui.',
    WebSharedWithOwlly: 'Creato con Owlly',
    WebSharedCtaTitle: 'Trasforma le note vocali in riassunti, quiz e flashcard',
    WebSharedCtaSub: 'Scarica Owlly gratis su iPhone, Android o sul web.',
    WebSharedOpenApp: 'Apri Owlly',
    WebShareNotFoundTitle: 'Questa nota non è disponibile',
    WebShareNotFoundBody: 'Il link potrebbe essere errato o il proprietario ha smesso di condividerla.',
    WebShareNote: 'Condividi link',
    WebStopSharing: 'Interrompi condivisione',
    WebStopSharingDone: 'Link pubblico rimosso',
  },
  ja: {
    WebLoginSubtitle:
      'サインインすると、どのデバイスからでもボイスノートにアクセスできます。',
    WebSignOut:
      'サインアウト',
    WebUpgradeOnMobile:
      'Owlly Proはモバイルアプリで購入できます。スマートフォンで購読し、同じアカウントでここにサインインすると、ウェブでもProが使えるようになります。',
    WebGetTheApp: 'モバイルアプリを入手',
    WebDropFiles: '音声ファイルまたはPDFをドロップしてノートを作成',
    WebDownloadAudio: '音声をダウンロード',
    WebExportPDF: 'PDFとして保存',
    WebMicDenied:
      'マイクへのアクセスが拒否されました。ブラウザの設定で許可してから、もう一度お試しください。',
    WebRecordingUnsupported: 'このブラウザはマイク録音に対応していません。',
    WebKeepTabOpen: 'アップロードが完了するまでこのタブを開いたままにしてください。',
    WebGuestAccount: 'ゲストアカウント',
    WebGuestWarning:
      'ノートはこのブラウザに保存されています。サインインして安全に保管し、スマートフォンでも使えるようにしましょう。',
    WebShortcutsHint: 'ショートカット: N 新規ノート · / 検索',
    WebSameAccountHint:
      'すでにスマートフォンで購読済みですか？同じAppleまたはGoogleアカウントでサインインすると、ここでもProが使えます。',
    WebSharedWithOwlly: 'Owllyで作成',
    WebSharedCtaTitle: '音声メモを要約・クイズ・フラッシュカードに変換',
    WebSharedCtaSub: 'iPhone、Android、WebでOwllyを無料で入手。',
    WebSharedOpenApp: 'Owllyを開く',
    WebShareNotFoundTitle: 'このノートは利用できません',
    WebShareNotFoundBody: 'リンクが無効か、所有者が共有を停止しました。',
    WebShareNote: 'リンクを共有',
    WebStopSharing: '共有を停止',
    WebStopSharingDone: '公開リンクを削除しました',
  },
  pt: {
    WebLoginSubtitle:
      'Entre para acessar suas notas de voz em qualquer dispositivo.',
    WebSignOut:
      'Sair',
    WebUpgradeOnMobile:
      'O Owlly Pro é comprado no app móvel do Owlly. Assine no seu telefone, entre aqui com a mesma conta e o Pro será desbloqueado também na web.',
    WebGetTheApp: 'Baixar o app móvel',
    WebDropFiles: 'Solte um arquivo de áudio ou PDF para criar uma nota',
    WebDownloadAudio: 'Baixar áudio',
    WebExportPDF: 'Salvar como PDF',
    WebMicDenied:
      'O acesso ao microfone foi negado. Permita nas configurações do navegador e tente novamente.',
    WebRecordingUnsupported:
      'Este navegador não suporta gravação pelo microfone.',
    WebKeepTabOpen: 'Mantenha esta aba aberta até o envio terminar.',
    WebGuestAccount: 'Conta de convidado',
    WebGuestWarning:
      'Suas notas estão vinculadas a este navegador. Entre para protegê-las e usá-las no seu telefone.',
    WebShortcutsHint: 'Atalhos: N nova nota · / buscar',
    WebSameAccountHint:
      'Já assinou no telefone? Entre com a mesma conta Apple ou Google para desbloquear o Pro aqui.',
    WebSharedWithOwlly: 'Feito com Owlly',
    WebSharedCtaTitle: 'Transforme notas de voz em resumos, quizzes e flashcards',
    WebSharedCtaSub: 'Baixe o Owlly grátis no iPhone, Android ou na web.',
    WebSharedOpenApp: 'Abrir Owlly',
    WebShareNotFoundTitle: 'Esta nota não está disponível',
    WebShareNotFoundBody: 'O link pode estar quebrado ou o dono parou de compartilhá-la.',
    WebShareNote: 'Compartilhar link',
    WebStopSharing: 'Parar de compartilhar',
    WebStopSharingDone: 'Link público removido',
  },
  ru: {
    WebLoginSubtitle:
      'Войдите, чтобы получить доступ к голосовым заметкам на любом устройстве.',
    WebSignOut:
      'Выйти',
    WebUpgradeOnMobile:
      'Owlly Pro приобретается в мобильном приложении Owlly. Оформите подписку на телефоне, войдите здесь с тем же аккаунтом — и Pro откроется и в вебе.',
    WebGetTheApp: 'Скачать мобильное приложение',
    WebDropFiles: 'Перетащите аудиофайл или PDF, чтобы создать заметку',
    WebDownloadAudio: 'Скачать аудио',
    WebExportPDF: 'Сохранить как PDF',
    WebMicDenied:
      'Доступ к микрофону запрещён. Разрешите его в настройках браузера и попробуйте снова.',
    WebRecordingUnsupported: 'Этот браузер не поддерживает запись с микрофона.',
    WebKeepTabOpen: 'Не закрывайте вкладку, пока загрузка не завершится.',
    WebGuestAccount: 'Гостевой аккаунт',
    WebGuestWarning:
      'Ваши заметки привязаны к этому браузеру. Войдите, чтобы сохранить их и использовать на телефоне.',
    WebShortcutsHint: 'Горячие клавиши: N — новая заметка · / — поиск',
    WebSameAccountHint:
      'Уже подписаны на телефоне? Войдите с тем же аккаунтом Apple или Google, чтобы открыть Pro здесь.',
    WebSharedWithOwlly: 'Создано в Owlly',
    WebSharedCtaTitle: 'Превращайте голосовые заметки в конспекты, тесты и карточки',
    WebSharedCtaSub: 'Скачайте Owlly бесплатно на iPhone, Android или в вебе.',
    WebSharedOpenApp: 'Открыть Owlly',
    WebShareNotFoundTitle: 'Эта заметка недоступна',
    WebShareNotFoundBody: 'Ссылка может быть неверной, или владелец прекратил доступ.',
    WebShareNote: 'Поделиться ссылкой',
    WebStopSharing: 'Закрыть доступ',
    WebStopSharingDone: 'Публичная ссылка удалена',
  },
  tr: {
    WebLoginSubtitle:
      'Sesli notlarınıza her cihazdan erişmek için giriş yapın.',
    WebSignOut:
      'Çıkış yap',
    WebUpgradeOnMobile:
      'Owlly Pro, Owlly mobil uygulamasından satın alınır. Telefonunuzda abone olun, burada aynı hesapla giriş yapın; Pro webde de açılır.',
    WebGetTheApp: 'Mobil uygulamayı indir',
    WebDropFiles: 'Not oluşturmak için bir ses dosyası veya PDF bırakın',
    WebDownloadAudio: 'Sesi İndir',
    WebExportPDF: 'PDF Olarak Kaydet',
    WebMicDenied:
      'Mikrofon erişimi reddedildi. Tarayıcı ayarlarından izin verip tekrar deneyin.',
    WebRecordingUnsupported: 'Bu tarayıcı mikrofonla kaydı desteklemiyor.',
    WebKeepTabOpen: 'Yükleme bitene kadar bu sekmeyi açık tutun.',
    WebGuestAccount: 'Misafir hesabı',
    WebGuestWarning:
      'Notlarınız bu tarayıcıya bağlı. Güvende tutmak ve telefonunuzda kullanmak için giriş yapın.',
    WebShortcutsHint: 'Kısayollar: N yeni not · / arama',
    WebSameAccountHint:
      'Telefonunuzda zaten abone misiniz? Pro’yu burada açmak için aynı Apple veya Google hesabıyla giriş yapın.',
    WebSharedWithOwlly: 'Owlly ile oluşturuldu',
    WebSharedCtaTitle: 'Sesli notlarını özetlere, quizlere ve bilgi kartlarına dönüştür',
    WebSharedCtaSub: "Owlly'yi iPhone, Android veya web'de ücretsiz edin.",
    WebSharedOpenApp: "Owlly'yi aç",
    WebShareNotFoundTitle: 'Bu not mevcut değil',
    WebShareNotFoundBody: 'Bağlantı hatalı olabilir ya da sahibi paylaşımı durdurmuş.',
    WebShareNote: 'Bağlantıyı paylaş',
    WebStopSharing: 'Paylaşımı durdur',
    WebStopSharingDone: 'Herkese açık bağlantı kaldırıldı',
  },
};
